import apiClient from "../../../utils/apiClient";

/**
 * studentAdapter — ExamRunner over the existing /student-assessments endpoints.
 *
 * This is what lets the enrolled-student exam and the public placement-drive
 * exam be the SAME screen. The runner never learns which one it is showing.
 *
 * Normalizes two things the student API shapes differently from the public one:
 *   - `antiCheat` (Assessment) → the runner's `policy` shape
 *   - the flat attempt payload → { attempt, questions, exam, policy }
 */
export default function createStudentAdapter({ attemptId }) {
  return {
    async loadAttempt() {
      const { data } = await apiClient.get(`/student-assessments/attempt/${attemptId}`);
      const a = data.attempt || {};
      const assessment = a.assessment || {};
      const ac = assessment.antiCheat || {};

      // Server-authoritative time, counted on the server's clock. Deriving it
      // here from expiresAt used this device's clock, so a student whose clock
      // ran slow kept answering past the deadline. The derivation stays only as
      // a fallback for a server that predates timeLeftSeconds.
      const timeLeftSeconds = a.timeLeftSeconds !== undefined
        ? a.timeLeftSeconds
        : a.expiresAt
          ? Math.max(0, Math.floor((new Date(a.expiresAt) - Date.now()) / 1000))
          : null;

      return {
        attempt: {
          _id: a._id,
          answers: a.answers || [],
          startedAt: a.startedAt,
          expiresAt: a.expiresAt,
          timeLeftSeconds,
          sectionProgress: a.sectionProgress || [],
          currentSectionIndex: a.currentSectionIndex || 0,
          // Present only when timed sections run in order.
          sectionClock: a.sectionClock || null,
        },
        questions: data.questions || [],
        passages: data.passages || [],
        exam: {
          title: assessment.title || "Assessment",
          instructions: assessment.instructions || "",
          isSectioned: !!assessment.isSectioned,
          negativeMarking: !!assessment.negativeMarking,
          negativeMarkValue: assessment.negativeMarkValue || 0,
        },
        // Map the Assessment's antiCheat block onto the runner's policy shape.
        // Only the signals this endpoint actually accepts are declared —
        // reporting a type the server rejects would just log errors.
        policy: {
          fullscreenRequired: !!ac.fullscreenRequired,
          disableCopyPaste: !!ac.disableCopyPaste,
          disableRightClick: !!ac.disableRightClick,
          blockPrint: false,
          watermark: false,
          heartbeatSeconds: 0, // no heartbeat endpoint on the student API
          signals: {
            ...(ac.tabSwitchDetection
              ? {
                tabSwitch: {
                  action: ac.autoSubmitOnViolation ? "autoSubmit" : "log",
                  threshold: ac.maxTabSwitches ?? 3,
                },
              }
              : {}),
            ...(ac.fullscreenRequired ? { fullscreenExit: { action: "log", threshold: 0 } } : {}),
            ...(ac.disableCopyPaste ? { copyPaste: { action: "log", threshold: 0 } } : {}),
            ...(ac.disableRightClick ? { rightClick: { action: "log", threshold: 0 } } : {}),
          },
        },
      };
    },

    async saveAnswer(questionId, patch) {
      const { data } = await apiClient.put(
        `/student-assessments/attempt/${attemptId}/answer`,
        { questionId, ...patch }
      );
      return data;
    },

    async reportEvent(type, meta = {}) {
      // The student endpoint speaks the legacy violation vocabulary. Anything
      // outside it is a signal this API does not record — drop it rather than
      // POSTing something the server will reject.
      const legacy = {
        tabSwitch: "tab_switch",
        fullscreenExit: "fullscreen_exit",
        rightClick: "right_click",
        copyPaste: "copy_paste",
      }[type];
      if (!legacy) return { recorded: false };

      const { data } = await apiClient.post(
        `/student-assessments/attempt/${attemptId}/violation`,
        { type: legacy, ...meta }
      );
      return {
        autoSubmitted: !!data?.autoSubmitted,
        warning: data?.autoSubmitted
          ? null
          : data?.tabSwitchCount
            ? `You left the exam window (${data.tabSwitchCount}). This has been recorded.`
            : null,
      };
    },

    // No heartbeat on the student API — the runner skips it when absent.
    heartbeat: null,

    /** A short-lived link to a passage's PDF, minted per request. */
    async passageFileUrl(passageId) {
      const { data } = await apiClient.get(
        `/student-assessments/attempt/${attemptId}/passages/${passageId}/file`
      );
      return data?.url || null;
    },

    /** Close the open timed section and open the next. Idempotent server-side. */
    async nextSection(from) {
      const { data } = await apiClient.post(
        `/student-assessments/attempt/${attemptId}/section/next`,
        { from }
      );
      return data;
    },

    /** Run code against a coding problem's sample tests, or the student's own input. */
    async runCode(questionId, body) {
      const { data } = await apiClient.post(
        `/student-assessments/attempt/${attemptId}/code/run`,
        { questionId, ...body }
      );
      return data;
    },

    async submit({ timeSpent }) {
      const { data } = await apiClient.post(
        `/student-assessments/attempt/${attemptId}/submit`,
        { timeSpent }
      );
      return data;
    },
  };
}
